<script setup>
import { ref, computed, onMounted } from 'vue';
import { crmmApi } from 'dashboard/helper/crmmApi';

const TZ_OFF = '-03:00';
const TZ = 'America/Sao_Paulo';
const board = ref({ agendas: [], servicos: [], agendamentos: [] });
const agendaId = ref('');
const svcId = ref('');
const dia = ref(new Date().toLocaleDateString('en-CA', { timeZone: TZ }));
const loading = ref(false);
const sincronizando = ref(false);
const error = ref('');
const slots = ref([]);
const selectedSlot = ref('');
const slotsHint = ref('Escolha o servico e clique em Ver horarios livres.');
const nome = ref('');
const tel = ref('');
const booking = ref(false);
const msg = ref(null);
const menuFor = ref('');
const panel = ref(null);
// Conexao Google por tenant (OAuth self-service). Endpoints diretos (GET/POST), nao via crmmApi.
const accountId = () => window.location.pathname.split('/')[3];
const gstatus = ref({ connected: false });
const gcals = ref(null);
const gmsg = ref('');
const gbase = () => '/api/v1/accounts/' + accountId() + '/crmm/google/';
async function loadGStatus() {
  try { const r = await window.axios.get(gbase() + 'status'); gstatus.value = r.data || { connected: false }; } catch (e) { gstatus.value = { connected: false }; }
}
async function conectarGoogle() {
  gmsg.value = 'Abrindo o Google...';
  try { const r = await window.axios.get(gbase() + 'connect'); if (r.data && r.data.url) window.location.href = r.data.url; else gmsg.value = 'OAuth nao configurado no servidor.'; }
  catch (e) { gmsg.value = 'Nao foi possivel iniciar a conexao com o Google.'; }
}
async function carregarCalendarios() {
  gmsg.value = 'Carregando calendarios...'; gcals.value = null;
  try { const r = await window.axios.get(gbase() + 'calendars'); gcals.value = (r.data && r.data.calendars) || []; gmsg.value = gcals.value.length ? '' : 'Nenhum calendario com permissao de escrita nesta conta.'; }
  catch (e) { gmsg.value = 'Erro ao listar calendarios.'; }
}
async function escolherCalendario(cal) {
  gmsg.value = 'Salvando...';
  try { await window.axios.post(gbase() + 'calendar', { calendar_id: cal.id }); gcals.value = null; gmsg.value = ''; await loadGStatus(); await loadBoard(); }
  catch (e) { gmsg.value = 'Erro ao salvar o calendario.'; }
}
async function desconectarGoogle() {
  try { await window.axios.delete(gbase() + 'disconnect'); gcals.value = null; await loadGStatus(); await loadBoard(); } catch (e) { gmsg.value = 'Erro ao desconectar.'; }
}

const hora = (iso) => new Date(iso).toLocaleTimeString('pt-BR', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });
const diaLabel = computed(() => new Date(dia.value + 'T12:00:00' + TZ_OFF).toLocaleDateString('pt-BR', { timeZone: TZ, weekday: 'long', day: '2-digit', month: 'short' }));
const bounds = () => { const ini = dia.value + 'T00:00:00' + TZ_OFF; return { ini, fim: new Date(new Date(ini).getTime() + 86400000).toISOString() }; };
const agendas = computed(() => board.value.agendas || []);
const servicos = computed(() => board.value.servicos || []);
const appts = computed(() => (board.value.agendamentos || []).filter((a) => a.agenda_id === agendaId.value));
const agendaSel = computed(() => agendas.value.find((a) => a.id === agendaId.value));
const BADGE = { google: { background: '#0e3a2a', color: '#5fe0a0' }, agendado: { background: '#11365e', color: '#7cc0ff' }, confirmado: { background: '#0e3a2a', color: '#5fe0a0' }, concluido: { background: '#22304a', color: '#8aa6c2' }, no_show: { background: '#3a1414', color: '#ff8080' } };
const badgeDe = (a) => (a.fonte === 'google' ? { t: 'Google', s: BADGE.google } : { t: a.status || 'agendado', s: BADGE[a.status] || BADGE.agendado });
const bordaDe = (a) => ({ borderLeft: '3px solid ' + (a.fonte === 'google' ? '#5fe0a0' : '#2ebdff') });

async function loadBoard() {
  loading.value = true; error.value = '';
  try {
    const b = bounds();
    const r = await crmmApi('agenda-board', { janela_inicio: b.ini, janela_fim: b.fim });
    board.value = (r && r.agendas) ? r : { agendas: [], servicos: [], agendamentos: [] };
    if (!agendas.value.some((a) => a.id === agendaId.value)) agendaId.value = (agendas.value[0] && agendas.value[0].id) || '';
    if (!servicos.value.some((s) => s.id === svcId.value)) svcId.value = (servicos.value[0] && servicos.value[0].id) || '';
  } catch (e) { error.value = String((e && e.message) || e); }
  finally { loading.value = false; }
}
const recarregar = () => { panel.value = null; menuFor.value = ''; slots.value = []; selectedSlot.value = ''; msg.value = null; loadBoard(); };
// Atualizar: dispara o sync Google->CRMM DESTE tenant (sob demanda) e recarrega — sem esperar o cron de 2min.
async function sincronizar() {
  if (sincronizando.value) return; sincronizando.value = true;
  try { await crmmApi('agenda-sync', {}); } catch (e) { /* segue: o board recarrega com o que ja existe */ }
  finally { sincronizando.value = false; }
  recarregar();
}
const mudaDia = (delta) => { const d = new Date(dia.value + 'T12:00:00' + TZ_OFF); d.setDate(d.getDate() + delta); dia.value = d.toLocaleDateString('en-CA', { timeZone: TZ }); recarregar(); };
const hoje = () => { dia.value = new Date().toLocaleDateString('en-CA', { timeZone: TZ }); recarregar(); };

async function verHorarios() {
  if (!agendaId.value || !svcId.value) { slotsHint.value = 'Sem agenda ou servico configurado.'; return; }
  slots.value = []; selectedSlot.value = ''; slotsHint.value = 'Carregando...';
  try {
    const r = await crmmApi('consultar-disponibilidade', { agenda_id: agendaId.value, service_id: svcId.value, janela_inicio: dia.value + 'T00:00:00' + TZ_OFF, janela_fim: dia.value + 'T23:59:00' + TZ_OFF });
    slots.value = (r && r.slots) || [];
    slotsHint.value = slots.value.length ? '' : 'Sem horarios livres neste dia.';
  } catch (e) { slotsHint.value = 'Erro ao buscar horarios.'; }
}
async function agendar(iso) {
  if (booking.value) return; booking.value = true;
  msg.value = { ok: true, t: 'Agendando...' };
  try {
    const r = await crmmApi('criar-agendamento', { agenda_id: agendaId.value, service_id: svcId.value, inicio: iso, nome_cliente: nome.value, telefone: tel.value });
    if (r && r.ok) { msg.value = { ok: true, t: 'Agendado: ' + new Date(r.inicio).toLocaleString('pt-BR', { timeZone: TZ }) }; await loadBoard(); await verHorarios(); }
    else { msg.value = { ok: false, t: 'Nao foi possivel agendar (horario ocupado?).' }; }
  } catch (e) { msg.value = { ok: false, t: 'Erro ao agendar.' }; }
  finally { booking.value = false; }
}

const abrirMenu = (id) => { menuFor.value = menuFor.value === id ? '' : id; };
function abrirPanel(a, mode) {
  menuFor.value = '';
  panel.value = { id: a.id, mode, nome: a.nome_cliente || '', tel: a.telefone || '', data: dia.value, svc: a.service_id || svcId.value, slots: [], hint: '', busy: false };
}
async function salvarEdicao() {
  const p = panel.value; if (!p) return; p.busy = true;
  try {
    const r = await crmmApi('editar-agendamento', { appointment_id: p.id, nome_cliente: p.nome, telefone: p.tel });
    if (r && r.ok) { panel.value = null; loadBoard(); } else { p.hint = 'Nao foi possivel salvar.'; }
  } catch (e) { p.hint = 'Erro ao salvar.'; } finally { if (panel.value) panel.value.busy = false; }
}
async function verSlotsPanel() {
  const p = panel.value; if (!p) return;
  if (!p.svc) { p.hint = 'Sem servico de referencia pra calcular horarios.'; return; }
  p.slots = []; p.hint = 'Carregando...';
  try {
    const r = await crmmApi('consultar-disponibilidade', { agenda_id: agendaId.value, service_id: p.svc, janela_inicio: p.data + 'T00:00:00' + TZ_OFF, janela_fim: p.data + 'T23:59:00' + TZ_OFF });
    p.slots = (r && r.slots) || [];
    p.hint = p.slots.length ? '' : 'Sem horarios livres nesse dia.';
  } catch (e) { p.hint = 'Erro ao buscar horarios.'; }
}
async function remarcarPara(iso) {
  const p = panel.value; if (!p) return; p.busy = true;
  try {
    const r = await crmmApi('remarcar-agendamento', { appointment_id: p.id, novo_inicio: iso });
    if (r && r.ok) { panel.value = null; loadBoard(); } else { p.hint = 'Horario indisponivel, tente outro.'; }
  } catch (e) { p.hint = 'Erro ao remarcar.'; } finally { if (panel.value) panel.value.busy = false; }
}
async function confirmarExclusao() {
  const p = panel.value; if (!p) return; p.busy = true;
  try {
    const r = await crmmApi('cancelar-agendamento', { appointment_id: p.id });
    if (r && r.ok) { panel.value = null; loadBoard(); } else { p.hint = 'Nao foi possivel excluir.'; }
  } catch (e) { p.hint = 'Erro ao excluir.'; } finally { if (panel.value) panel.value.busy = false; }
}
onMounted(async () => { await loadGStatus(); if (new URLSearchParams(window.location.search).get('google') === 'connected' && !gstatus.value.calendar_id) carregarCalendarios(); loadBoard(); });
</script>

<template>
  <div class="flex flex-col h-full bg-n-background text-n-slate-12 p-4 overflow-hidden" @click="menuFor = ''">
    <div class="flex items-center justify-between mb-4 flex-wrap gap-2">
      <div class="flex items-center gap-3">
        <h1 class="text-xl font-semibold text-n-slate-12">Agenda</h1>
        <select v-if="agendas.length" v-model="agendaId" class="bg-n-solid-2 text-n-slate-12 text-sm rounded-lg px-2 py-1.5 outline outline-1 outline-n-weak" @change="recarregar">
          <option v-for="a in agendas" :key="a.id" :value="a.id">{{ a.nome }}</option>
        </select>
        <span v-if="agendaSel && agendaSel.google" class="text-[10px] font-bold px-2 py-0.5 rounded-md" :style="BADGE.google">Google sincronizado</span>
      </div>
      <div class="flex items-center gap-2">
        <button class="text-sm px-2.5 py-1.5 rounded-lg bg-n-solid-3 text-n-slate-12 hover:bg-n-alpha-2" @click="mudaDia(-1)">&lsaquo;</button>
        <input v-model="dia" type="date" class="bg-n-solid-2 text-n-slate-12 text-sm rounded-lg px-2 py-1.5 outline outline-1 outline-n-weak" @change="recarregar" />
        <button class="text-sm px-2.5 py-1.5 rounded-lg bg-n-solid-3 text-n-slate-12 hover:bg-n-alpha-2" @click="mudaDia(1)">&rsaquo;</button>
        <button class="text-xs px-3 py-1.5 rounded-lg bg-n-solid-3 text-n-slate-12 hover:bg-n-alpha-2" @click="hoje">Hoje</button>
        <button class="text-xs px-3 py-1.5 rounded-lg bg-n-solid-3 text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50" :disabled="sincronizando" @click="sincronizar">{{ sincronizando ? 'Sincronizando...' : 'Atualizar' }}</button>
      </div>
    </div>
    <div class="mb-3 flex items-center gap-3 flex-wrap bg-n-solid-2 rounded-xl outline outline-1 outline-n-container px-3 py-2">
      <template v-if="!gstatus.connected">
        <span class="text-xs text-n-slate-11">Conecte o Google desta conta pra espelhar os agendamentos no Google Calendar.</span>
        <button class="text-xs px-3 py-1.5 rounded-lg bg-n-brand text-white hover:opacity-90" @click="conectarGoogle">Conectar Google</button>
      </template>
      <template v-else>
        <span class="text-xs text-n-slate-12">Google: <b>{{ gstatus.email || 'conectado' }}</b><span v-if="gstatus.calendar_id" class="text-n-slate-11"> · calendário vinculado</span></span>
        <button class="text-xs px-2.5 py-1 rounded-lg bg-n-solid-3 text-n-slate-12 hover:bg-n-alpha-2" @click="carregarCalendarios">{{ gstatus.calendar_id ? 'Trocar calendário' : 'Escolher calendário' }}</button>
        <button class="text-xs px-2.5 py-1 rounded-lg bg-n-solid-3 text-n-ruby-11 hover:bg-n-alpha-2" @click="desconectarGoogle">Desconectar</button>
      </template>
      <span v-if="gmsg" class="text-xs text-n-slate-11">{{ gmsg }}</span>
      <div v-if="gcals" class="w-full flex flex-wrap gap-1.5 pt-1">
        <button v-for="c in gcals" :key="c.id" class="text-xs px-2.5 py-1 rounded-lg bg-n-solid-3 text-n-slate-12 hover:bg-n-brand hover:text-white" @click="escolherCalendario(c)">{{ c.summary }}<span v-if="c.primary"> (principal)</span></button>
        <button class="text-xs px-2.5 py-1 rounded-lg bg-n-solid-1 text-n-slate-11" @click="gcals = null">cancelar</button>
      </div>
    </div>
    <div v-if="loading" class="text-n-slate-11">Carregando agenda...</div>
    <div v-else-if="error" class="text-n-ruby-11">Erro: {{ error }}</div>
    <div v-else-if="!agendas.length" class="bg-n-solid-2 rounded-xl outline outline-1 outline-n-container p-6 max-w-lg">
      <div class="text-sm font-medium text-n-slate-12 mb-1">Nenhuma agenda configurada</div>
      <div class="text-xs text-n-slate-11">Esta conta ainda nao tem agenda. Fale com o administrador do CRMM IA para configurar a agenda e os servicos.</div>
    </div>
    <div v-else class="flex gap-4 flex-1 min-h-0">
      <div class="flex flex-col flex-1 min-w-0 bg-n-solid-2 rounded-xl outline outline-1 outline-n-container p-3">
        <div class="flex items-center justify-between px-1 pb-2 border-b border-n-weak mb-2">
          <span class="font-semibold text-sm capitalize">{{ diaLabel }}</span>
          <span class="text-xs text-n-slate-11">{{ appts.length }} agendamento(s)</span>
        </div>
        <div class="flex flex-col gap-2 overflow-y-auto flex-1">
          <div v-for="a in appts" :key="a.id" class="relative bg-n-solid-1 rounded-lg p-2.5 outline outline-1 outline-n-weak" :style="bordaDe(a)">
            <div class="flex items-start justify-between gap-2">
              <div class="min-w-0">
                <div class="text-sm font-semibold text-n-slate-12">{{ hora(a.inicio) }}&ndash;{{ hora(a.fim) }} <span class="font-medium">{{ a.nome_cliente || '(sem nome)' }}</span></div>
                <div class="text-xs text-n-slate-11 mt-0.5 truncate">{{ a.servico || 'servico' }} &middot; {{ a.telefone || 's/ telefone' }}</div>
              </div>
              <div class="flex items-center gap-2 shrink-0">
                <span class="text-[10px] font-bold px-2 py-0.5 rounded-md uppercase" :style="badgeDe(a).s">{{ badgeDe(a).t }}</span>
                <button class="text-n-slate-11 hover:text-n-slate-12 px-1.5 rounded-md hover:bg-n-alpha-2" @click.stop="abrirMenu(a.id)">&#8942;</button>
              </div>
            </div>
            <div v-if="menuFor === a.id" class="absolute right-2 top-9 z-20 bg-n-solid-2 rounded-lg outline outline-1 outline-n-container shadow-xl overflow-hidden min-w-[130px]" @click.stop>
              <button class="block w-full text-left text-sm px-3 py-2 hover:bg-n-alpha-2 text-n-slate-12" @click="abrirPanel(a, 'editar')">Editar</button>
              <button class="block w-full text-left text-sm px-3 py-2 hover:bg-n-alpha-2 text-n-slate-12" @click="abrirPanel(a, 'remarcar')">Remarcar</button>
              <button class="block w-full text-left text-sm px-3 py-2 hover:bg-n-alpha-2 text-n-ruby-11" @click="abrirPanel(a, 'excluir')">Excluir</button>
            </div>
            <div v-if="panel && panel.id === a.id" class="mt-2 pt-2 border-t border-n-weak flex flex-col gap-2" @click.stop>
              <template v-if="panel.mode === 'editar'">
                <label class="text-xs text-n-slate-11 flex flex-col gap-1">Nome do cliente<input v-model="panel.nome" class="w-full bg-n-alpha-black2 rounded-lg px-2 py-1.5 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand" /></label>
                <label class="text-xs text-n-slate-11 flex flex-col gap-1">Telefone<input v-model="panel.tel" class="w-full bg-n-alpha-black2 rounded-lg px-2 py-1.5 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand" /></label>
                <div class="flex gap-2"><button :disabled="panel.busy" class="text-xs px-3 py-1.5 rounded-lg bg-n-brand text-white disabled:opacity-40" @click="salvarEdicao">Salvar</button><button class="text-xs px-3 py-1.5 rounded-lg bg-n-solid-3 text-n-slate-12" @click="panel = null">Cancelar</button></div>
              </template>
              <template v-else-if="panel.mode === 'remarcar'">
                <div class="flex items-end gap-2 flex-wrap">
                  <label class="text-xs text-n-slate-11 flex flex-col gap-1">Nova data<input v-model="panel.data" type="date" class="bg-n-alpha-black2 rounded-lg px-2 py-1.5 text-sm text-n-slate-12 outline outline-1 outline-n-weak" /></label>
                  <button class="text-xs px-3 py-1.5 rounded-lg bg-n-brand text-white" @click="verSlotsPanel">Ver horarios</button>
                  <button class="text-xs px-3 py-1.5 rounded-lg bg-n-solid-3 text-n-slate-12" @click="panel = null">Cancelar</button>
                </div>
                <div v-if="panel.slots.length" class="flex flex-wrap gap-1.5">
                  <button v-for="s in panel.slots" :key="s" :disabled="panel.busy" class="text-xs px-2.5 py-1.5 rounded-lg bg-n-solid-3 text-n-slate-12 hover:bg-n-brand hover:text-white disabled:opacity-40" @click="remarcarPara(s)">{{ hora(s) }}</button>
                </div>
              </template>
              <template v-else>
                <div class="text-xs text-n-slate-12">Excluir o agendamento de {{ a.nome_cliente || 'cliente' }}? <span v-if="a.fonte === 'google'">O evento sera removido do Google tambem.</span></div>
                <div class="flex gap-2"><button :disabled="panel.busy" class="text-xs px-3 py-1.5 rounded-lg bg-n-ruby-9 text-white disabled:opacity-40" @click="confirmarExclusao">Confirmar exclusao</button><button class="text-xs px-3 py-1.5 rounded-lg bg-n-solid-3 text-n-slate-12" @click="panel = null">Cancelar</button></div>
              </template>
              <div v-if="panel.hint" class="text-xs text-n-ruby-11">{{ panel.hint }}</div>
            </div>
          </div>
          <div v-if="!appts.length" class="text-xs text-n-slate-10 px-1 py-3 text-center">Nenhum agendamento neste dia.</div>
        </div>
      </div>
      <div class="flex flex-col w-80 shrink-0 bg-n-solid-2 rounded-xl outline outline-1 outline-n-container p-3 overflow-y-auto">
        <div class="font-semibold text-sm px-1 pb-2 border-b border-n-weak mb-2">Novo agendamento</div>
        <label class="text-xs text-n-slate-11 flex flex-col gap-1 mb-2">Servico
          <select v-model="svcId" class="w-full bg-n-alpha-black2 rounded-lg px-2 py-1.5 text-sm text-n-slate-12 outline outline-1 outline-n-weak">
            <option v-for="s in servicos" :key="s.id" :value="s.id">{{ s.nome }} ({{ s.duracao_min }} min)</option>
          </select>
        </label>
        <button class="text-sm px-3 py-2 rounded-lg bg-n-brand text-white hover:opacity-90 mb-2" @click="verHorarios">Ver horarios livres</button>
        <div v-if="slotsHint" class="text-xs text-n-slate-11 mb-2">{{ slotsHint }}</div>
        <div v-if="slots.length" class="flex flex-wrap gap-1.5 mb-2">
          <button v-for="s in slots" :key="s" class="text-xs px-2.5 py-1.5 rounded-lg" :class="selectedSlot === s ? 'bg-n-brand text-white' : 'bg-n-solid-3 text-n-slate-12 hover:bg-n-brand hover:text-white'" @click="selectedSlot = s">{{ hora(s) }}</button>
        </div>
        <label class="text-xs text-n-slate-11 flex flex-col gap-1 mb-2">Nome do cliente<input v-model="nome" placeholder="Ex: Maria" class="w-full bg-n-alpha-black2 rounded-lg px-2 py-1.5 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand" /></label>
        <label class="text-xs text-n-slate-11 flex flex-col gap-1 mb-2">Telefone<input v-model="tel" placeholder="5599999999999" class="w-full bg-n-alpha-black2 rounded-lg px-2 py-1.5 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand" /></label>
        <button v-if="slots.length" :disabled="!selectedSlot || booking" class="text-sm px-3 py-2 rounded-lg bg-n-brand text-white hover:opacity-90 disabled:opacity-40 mb-2" @click="agendar(selectedSlot)">{{ booking ? 'Agendando...' : (selectedSlot ? 'Agendar ' + hora(selectedSlot) : 'Selecione um horario') }}</button>
        <div class="text-xs text-n-slate-10 mb-1">Selecione um horario livre e clique em Agendar.</div>
        <div v-if="msg" class="text-xs rounded-lg px-2 py-1.5" :style="msg.ok ? { background: '#0e3a2a', color: '#5fe0a0' } : { background: '#3a1414', color: '#ff8080' }">{{ msg.t }}</div>
      </div>
    </div>
  </div>
</template>
